import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, NavBar, Skeleton, Toast} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";

import PhotoUploadCard from '../components/PhotoUploadCard';
import PhotoViewer from '../components/PhotoViewer';
import MultiPhotoSelectorView from '../components/MultiPhotoSelectorView';
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import InfoItem from '../components/InfoItem';
import photoService, {PhotoType, PositionType} from '../services/PhotoService';
import multiPhotoService from '../services/MultiPhotoService';
import {NativePermissions} from '../components/PhotoNativeBridge';
import DeliveryDataService, {FailedDeliveryOrder, uploadPhotosWithTimeout} from "../../../services/DeliveryDataService";
import {PhotoProcessingUtils} from "../utils/PhotoProcessingUtils";
import {PhotoUploadManager} from "../utils/PhotoUploadManager";
import {DeliveringPath} from "../../../enums/DeliveryNavigatePathEnum";
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {useDeliveryTaskOrder, useDeliveryTaskOrderOperations, useFromMapMode} from '../store/deliveryStore';
import {ExceptionDictionaryType} from "../graphql/queries/getExceptionDictionaryForDelivery";
import {useLocationStore} from "../../../store/locationStore";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import {t} from "../../../i18n";
import {useTranslation} from "react-i18next";
import { getExceptionTypeText } from '../../../utils/i18nUtils';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import { openNativeMapMode } from 'utils/appRuntime';
import geolocationService, {GeoPosition} from "../services/GeolocationService";
import {openCamera} from "../../../utils/appRuntime";
import {isAppVersionGreaterThanOrEqual, kNativeMultiImagePickerVersion} from "../../../utils/versionUtils";
import { getUserInfo } from '../../../utils/auth';
import {maskPhoneNumber} from "../../../utils/phoneUtils";

interface MarkExceptionProps {
}

// 最大照片数量
const kMaxSelectorPhotoLength = 6;

// 根据异常原因获取拍照配置
const getExceptionPhotoConfig = (exceptionReasonCode: string) => {
    // 根据异常原因代码或中文描述匹配
    const code = exceptionReasonCode || '';

    // 地址异常
    if (code === "ERDL001") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.detail.exceptionPhoto.addressException.detailPagePrompt'),
            cameraText: t('pages.delivery.detail.exceptionPhoto.addressException.cameraPagePrompt')
        };
    }
    // 无法进入派件区域
    if (code === "ERDL002") {
        return {
            minPhotos: 2,
            requirementText:t('pages.delivery.detail.exceptionPhoto.noAccess.detailPagePrompt'),
            cameraText: t('pages.delivery.detail.exceptionPhoto.noAccess.cameraPagePrompt')
        };
    }
    // 商户未营业
    if (code === "ERDL003") {
        return {
            minPhotos: 3,
            requirementText:t('pages.delivery.detail.exceptionPhoto.notInBusiness.detailPagePrompt'),
            cameraText:t('pages.delivery.detail.exceptionPhoto.notInBusiness.cameraPagePrompt'),
        };
    }
    // 收件人拒收
    if (code === "ERDL004") {
        return {
            minPhotos: 2,
            requirementText: t('pages.delivery.detail.exceptionPhoto.refusedFreight.detailPagePrompt'),
            cameraText: t('pages.delivery.detail.exceptionPhoto.refusedFreight.cameraPagePrompt')
        };
    }

    // 默认配置
    return {
        minPhotos: 2,
        requirementText: '请至少拍摄2张照片',
        cameraText: '请拍摄照片'
    };
};

const MarkExceptionPage: React.FC<MarkExceptionProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {i18n} = useTranslation();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [showMultiPhotoSelector, setShowMultiPhotoSelector] = useState<boolean>(false); // 异常页面默认使用新版本连拍
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {position} = useLocationStore();
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const trackingNumber = location.state?.trackingNumber || '';
    const exceptionReason: ExceptionDictionaryType = location.state?.exceptionReason;
    const fromNativeMapMode = location.state?.fromNativeMapMode || false;
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const {handleDeliveryFailed} = useDeliveryTaskOrderOperations();
    const {fromMapMode, setFromMapMode} = useFromMapMode();
    const userInfo = getUserInfo();
    
    // 检查是否来自地图模式（Web地图）
    const isFromMapMode = fromMapMode || location.state?.fromMapMode;
    // 检查是否来自原生地图模式
    const isFromNativeMapMode = fromNativeMapMode;

    // 获取异常原因的拍照配置
    const photoConfig = getExceptionPhotoConfig(exceptionReason?.exceptionReasonCode || '');

    sessionStorage.setItem('returningFromDetail', 'true');
    useEffect(() => {
        if (!deliveryTaskOrder) return;
        const podImages = deliveryTaskOrder.orderWithAddress?.order?.exceptionImageS3UrlsJson || [];
        const podImageKeys = deliveryTaskOrder?.orderWithAddress?.order?.exceptionImageS3KeysJson || [];

        if (showMultiPhotoSelector) {
            // 新版本：数组形式
            if (podImages.length > 0 && podImageKeys.length > 0) {
                setPhotosArray(podImages.map((url, index) => ({
                    url,
                    objectKey: podImageKeys[index] || ''
                })));
            }
        } else {
            // 老版本：固定位置
            if (podImages.length > 0 && podImageKeys.length > 0) {
                setPhotos({
                    label: {
                        url: podImages[0],
                        objectKey: podImageKeys[0]
                    },
                    location: {
                        url: podImages[1],
                        objectKey: podImageKeys[1]
                    },
                    houseNumber: undefined
                });
            }
        }
    }, [deliveryTaskOrder, showMultiPhotoSelector]);

    // 版本检查
    useEffect(() => {
        const checkVersion = async () => {
            const isHigher = await isAppVersionGreaterThanOrEqual(kNativeMultiImagePickerVersion);
            setShowMultiPhotoSelector(isHigher);
        };

        checkVersion();
    }, []);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 保存订单数据到本地
    const saveOrderToLocal = async (
        customPhotosArray?: PhotoType[],
        customPhotos?: Record<PositionType, PhotoType | undefined>,
        customCompletionLocation?: GeoPosition | null
    ): Promise<boolean> => {
        try {
            // 使用传入的参数或当前状态
            const currentPhotosArray = customPhotosArray || photosArray;
            const currentPhotos = customPhotos || photos;

            const order: FailedDeliveryOrder = {
                trackingNumber: trackingNumber,
                updatedAt: Date.now(),
                photos: exceptionReason.isUploadsImages
                    ? PhotoProcessingUtils.processPhotosForSaving(currentPhotosArray, currentPhotos, showMultiPhotoSelector)
                    : [],
                location: position,
                stopNumber: deliveryTaskOrder?.stopNumber?.toString() || '0',
                subStopNumber: deliveryTaskOrder?.subStopNumber,
                id: deliveryTaskOrderId,
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                type: 'failed',
                // ✅ 新增字段：保存完成时GPS
                completionLocation: customCompletionLocation
            };

            // 保存到本地
            const saved = await DeliveryDataService.saveDeliveryOrder(order);
            return saved;
        } catch (error) {
            console.error('Mark Exception to save order to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PositionType, file: File, source: 'camera' | 'gallery') => {
        try {
            setIsLoading(true);
            let currentPosition: GeoPosition | null = {
                latitude: 0,
                longitude: 0,
                accuracy: 0,
                timestamp: 0
            };
            
            // 传递位置信息和来源
            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            }, currentPosition, source);
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片 - 老版本
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.exception.deletePhotoTitle'),
            confirmText: t('pages.delivery.exception.deletePhotoDelete'),
            cancelText: t('pages.delivery.exception.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 删除照片 - 新版本（数组）
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.exception.deletePhotoTitle'),
            confirmText: t('pages.delivery.exception.deletePhotoDelete'),
            cancelText: t('pages.delivery.exception.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - currentPhotoCount;

        openCamera(remainingPhotoCount, minSelectionCount, photoConfig.cameraText, async (photos: string[]) => {
            try {
                // ✅ 连拍时为每张照片获取实时位置（每张照片一个经纬度）
                const photosWithPosition: Array<{ photo: string; position: GeoPosition | null }> = [];

                for (const photo of photos) {
                    let currentPosition: GeoPosition | null = {
                        latitude: 0,
                        longitude: 0,
                        accuracy: 0,
                        timestamp: 0,
                    };
                    photosWithPosition.push({photo, position: currentPosition});
                }

                await multiPhotoService.handleMultiPhotoSelectionArrayWithIndividualPositions(
                    photosWithPosition,
                    (updateFn) => setPhotosArray(updateFn),
                    'camera' // 连拍来源：拍照
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({content: '多图片处理失败', position: 'center'});
            }
        });
    };

    /*照片相关操作 end======*/

    // 标记异常
    const handleCompleteException = async () => {
        setIsLoading(true);

        // 创建照片上传管理器（根据showMultiPhotoSelector决定使用新版本还是老版本）
        const photoUploadManager = new PhotoUploadManager(
            showMultiPhotoSelector, // 使用新版本连拍
            deliveryTaskOrder?.trackingNumber || trackingNumber,
            setPhotosArray,
            setPhotos
        );

        // ✅ 获取完成时的实时经纬度（用于 location 字段，与照片GPS独立）
        let completionPosition = position; // 默认使用缓存位置

        try {
            // 验证照片数量（根据新版本或老版本）
            if (showMultiPhotoSelector) {
                // 新版本：检查数组照片数量
                if (exceptionReason.isUploadsImages && (photosArray.length < photoConfig.minPhotos || !photosArray.every(photo => photo.url))) {
                    Toast.show({
                        content: t('pages.delivery.detail.takeAtLeastPhotos', { count: photoConfig.minPhotos }),
                        position: 'center'
                    });
                    setIsLoading(false);
                    return;
                }
            } else {
                // 老版本：检查固定位置照片
                const hasLabel = photos.label?.objectKey;
                const hasLocation = photos.location?.objectKey;
                if (exceptionReason.isUploadsImages && (!hasLabel || !hasLocation)) {
                    Toast.show({content: 'Please add a photo of the cause of the anomaly', position: 'center'});
                    setIsLoading(false);
                    return;
                }
            }

            // 获取当前照片状态
            const currentPhotos = showMultiPhotoSelector ? [...photosArray] : {...photos};
            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;

            // 获取完成时的实时经纬度（强制获取最新位置，不使用缓存）
            try {
                completionPosition = await geolocationService.getFreshPosition();
                console.log('📍 异常完成时获取到最新位置:', completionPosition);
            } catch (error) {
                console.warn('⚠️ 获取位置失败，使用缓存位置:', error);
            }


            // 格式化经纬度，保留7位小数（精度约1.1厘米）
            const formatCoordinate = (coord: number): number => {
                if (coord === 0) {
                    return 0;
                }
                return parseFloat(coord.toFixed(7));
            };

            // 准备照片信息（按来源分组）
            // 准备初始照片信息（乐观提交用，使用 objectKey）
            const prepareExceptionImagesInfo = (): {
                cameraPhotos: Array<{ s3Key: string, latitude: number, longitude: number }>;
                galleryPhotos: { latitude: number, longitude: number } | null;
            } => {
                const cameraPhotos: Array<{ s3Key: string, latitude: number, longitude: number }> = [];
                let firstGalleryPhotoGPS: { latitude: number, longitude: number } | null = null;
                let hasGalleryPhotos = false;
                
                if (exceptionReason.isUploadsImages) {
                    photosArray.forEach(photo => {
                        if (!photo?.objectKey) return;
                        
                        if (photo.source === 'camera') {
                            // ✅ 拍照：使用照片自己保存的经纬度
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        } else if (photo.source === 'gallery') {
                            // ✅ 记录第一张相册照片的GPS（如果需要使用uploadPhotos字段）
                            if (!firstGalleryPhotoGPS) {
                                firstGalleryPhotoGPS = {
                                    latitude: formatCoordinate(photo.latitude || 0),
                                    longitude: formatCoordinate(photo.longitude || 0)
                                };
                            }
                            hasGalleryPhotos = true;
                        } else {
                            // 如果没有 source，默认为拍照
                            cameraPhotos.push({
                                s3Key: photo.objectKey,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        }
                    });
                }
                
                return {
                    cameraPhotos,
                    galleryPhotos: (hasGalleryPhotos && firstGalleryPhotoGPS) ? firstGalleryPhotoGPS : null
                };
            };
            
            const initialExceptionImagesInfo = prepareExceptionImagesInfo();
            
            // ✅ 准备 S3 keys 数组（兼容旧版本接口）
            const initialObjectKeys = exceptionReason.isUploadsImages
                ? (showMultiPhotoSelector
                    ? photosArray.map(photo => photo.objectKey).filter((key): key is string => !!key)
                    : [photos?.label?.objectKey, photos?.location?.objectKey].filter((key): key is string => !!key))
                : [];

            // 仅执行乐观提交逻辑，不进行网络请求
            await handleDeliveryFailed({
                trackingNumber: currentTrackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,
                    longitude: completionPosition?.longitude || 0.0
                },
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                id: deliveryTaskOrderId,
                exceptionImageS3KeysJson: initialObjectKeys, // ✅ 保留旧字段
                cameraPhotos: initialExceptionImagesInfo.cameraPhotos, // ✅ 新增字段
                uploadPhotos: initialExceptionImagesInfo.galleryPhotos, // ✅ 新增字段
            }, true);

            // 恢复照片状态
            if (showMultiPhotoSelector) {
                setPhotosArray(currentPhotos as PhotoType[]);
            } else {
                setPhotos(currentPhotos as Record<PositionType, PhotoType | undefined>);
            }

            // 如果需要上传图片，使用PhotoUploadManager
            let photoKeys: string[] = [];
            let photosToUpload: any[] = [];
            if (exceptionReason.isUploadsImages) {
                // 初始化照片数据并获取需要上传的照片（根据新版本或老版本）
                photoUploadManager.initializeLocalCopies(photosArray, photos);
                photosToUpload = photoUploadManager.getPhotosToUpload(photosArray, photos);
                // 执行照片上传
                photoKeys = await photoUploadManager.uploadPhotos(photosToUpload);
            }

            // 准备最新的照片信息（上传后，使用真实的 S3 keys）
            const prepareFinalExceptionImagesInfo = (): {
                cameraPhotos: Array<{s3Key: string, latitude: number, longitude: number}>;
                galleryPhotos: {latitude: number, longitude: number} | null;
            } => {
                const cameraPhotos: Array<{s3Key: string, latitude: number, longitude: number}> = [];
                let firstGalleryPhotoGPS: {latitude: number, longitude: number} | null = null;
                let hasGalleryPhotos = false;
                
                if (exceptionReason.isUploadsImages) {
                    // 保证key 和 照片的对应关系
                    photosToUpload.forEach((photo, index) => {
                        const s3Key = photoKeys[index];
                        if (!s3Key) return;
                        
                        if (photo.source === 'camera') {
                            // 通过拍照获取的照片经纬度
                            cameraPhotos.push({
                                s3Key: s3Key,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        } else if (photo.source === 'gallery') {
                            // 通过相册选择的照片经纬度
                            if (!firstGalleryPhotoGPS) {
                                firstGalleryPhotoGPS = {
                                    latitude: formatCoordinate(photo.latitude || 0),
                                    longitude: formatCoordinate(photo.longitude || 0)
                                };
                            }
                            hasGalleryPhotos = true;
                        } else {
                            // 如果没有 source，默认为拍照
                            cameraPhotos.push({
                                s3Key: s3Key,
                                latitude: formatCoordinate(photo.latitude || 0),
                                longitude: formatCoordinate(photo.longitude || 0)
                            });
                        }
                    });
                }
                
                return {
                    cameraPhotos,
                    galleryPhotos: (hasGalleryPhotos && firstGalleryPhotoGPS) ? firstGalleryPhotoGPS : null
                };
            };
            
            const finalExceptionImagesInfo = prepareFinalExceptionImagesInfo();

            //真正请求后端接口 标记异常（使用完成时获取的经纬度）
            await handleDeliveryFailed({
                trackingNumber: currentTrackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,  // ✅ 使用完成时的GPS
                    longitude: completionPosition?.longitude || 0.0  // ✅ 使用完成时的GPS
                },
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                id: deliveryTaskOrderId,
                exceptionImageS3KeysJson: photoKeys,
                cameraPhotos: finalExceptionImagesInfo.cameraPhotos,
                uploadPhotos: finalExceptionImagesInfo.galleryPhotos,
            }, false);

            if (isFromNativeMapMode) {
                Toast.show({ content: t('pages.delivery.exception.markAsFailed'), position: 'center' });
            }

            // 根据导航来源决定返回路径
            if (isFromNativeMapMode) {
                // 从原生地图跳转过来，返回后需要重新打开原生地图
                openNativeMapMode()
                navigateBack.navigateToDeliveringList(navigate);
            } else if (isFromMapMode) {
                setFromMapMode(false); // 清除导航来源
                sessionStorage.setItem('returningFromDetail', 'true');
                navigateBack.navigateToLsatPage(navigate);
            } else {
                navigateBack.navigateToDeliveringList(navigate);
            }
        } catch (error: any) {
            if (error.networkError) {
                // ✅ 获取最新的照片数据并保存到本地缓存（照片本身包含GPS信息）
                const { photosArray: latestPhotosArray, photos: latestPhotos } = photoUploadManager.getLatestPhotosData();
                // ✅ 直接保存照片和完成时GPS，照片的经纬度会在重试时从照片本身提取
                await saveOrderToLocal(
                    latestPhotosArray,
                    latestPhotos,
                    completionPosition // 保存完成时的GPS
                );

                if (isFromNativeMapMode) {
                    // 从原生地图跳转过来，返回后需要重新打开原生地图
                    openNativeMapMode()
                    navigateBack.navigateToDeliveringList(navigate);
                } else if (isFromMapMode) {
                    setFromMapMode(false);
                    sessionStorage.setItem('returningFromDetail', 'true');
                    navigateBack.navigateToLsatPage(navigate);
                } else {
                    navigateBack.navigateToDeliveringList(navigate);
                }
            } else {
                console.error('Mark failed:', error);
                Toast.show({content: t('pages.delivery.orderDetail.markFailed'), position: 'center'});
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode()
                            navigateBack.navigateToDeliveringList(navigate);
                        } else {
                            navigate(-1);
                        }
                    }}>
                        Failed
                    </NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => {
                        if (isFromNativeMapMode) {
                            openNativeMapMode()
                            navigateBack.navigateToDeliveringList(navigate);
                        } else {
                            navigate(-1);
                        }
                    }}>
                        Failed
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const address = `${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || ''} 
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || ''}
                            ${deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || ''} `.trim();
    const stopNumber = deliveryTaskOrder.stopNumber;
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phone = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;

    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= photoConfig.minPhotos ? 0 : photoConfig.minPhotos - currentPhotoCount;
        const cameraText = photoConfig.cameraText;
        // 原生端：使用 MultiPhotoSelectorView
        return (
            <MultiPhotoSelectorView
                minPhotos={minSelectionCount}
                maxPhotos={remainingPhotoCount}
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        let currentPosition: GeoPosition | null = {
                            latitude: 0,
                            longitude: 0,
                            accuracy: 0,
                            timestamp: 0,
                        };

                        const result = await multiPhotoService.handleMultiPhotoSelectionArray(
                            photos,
                            (updateFn) => setPhotosArray(updateFn),
                            currentPosition,
                            'gallery' // 相册选择来源：相册
                        );

                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                            // 可以显示提示信息
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                        Toast.show({content: '多图片处理失败', position: 'center'});
                    }
                }}
                cameraText = {cameraText}
            />
        );
    })()


    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    if (isFromNativeMapMode) {
                        openNativeMapMode()
                        navigateBack.navigateToDeliveringList(navigate);
                    } else {
                        navigate(-1);
                    }
                }}>{t('pages.delivery.orderDetail.failed')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.orderInformation')}</h2>
                    <div className="mb-1">
                        <InfoItem label={`${t('pages.delivery.detail.trackingNumber')}`} value={trackingNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.stopNo')}`} value={deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0'}/>
                        <InfoItem label={`${t('pages.delivery.detail.routeNo')}`} value={routeNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.assignedTime')}`}
                                  value={deliverAt ? timestampToDate(deliverAt).toLocaleString() : ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.deliveryAttempt')}`}
                                  value={`${deliveryAttempts} out of 3`}/>
                        <OrderNoteItem
                            orderNote={deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                        <div className="flex justify-between py-2 border-gray-100">
                            <span
                                className="text-gray-600 text-sm">{`${t('pages.delivery.exception.filedReason')}`}:</span>
                            <span
                                className="text-sm font-medium text-right text-violet-600">{getExceptionTypeText(exceptionReason)}</span>
                        </div>
                    </div>
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.customerInformation')}</h2>
                    <div>
                        <InfoItem label={`${t('pages.delivery.detail.name')}`} value={fullName || ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.phoneNumber')}`} value={maskPhoneNumber(phone) || ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.address')}`} value={address}/>
                    </div>
                </div>

                <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {!showMultiPhotoSelector
                            ? t('pages.delivery.exception.photoDesc')
                            : photoConfig.requirementText
                        }
                    </p>
                    {/* 老版本：固定位置拍照 */}
                    {!showMultiPhotoSelector && <div className="grid grid-cols-3 gap-4">
                        <PhotoUploadCard
                            photoUrl={photos.label?.url}
                            type="label"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PhotoUploadCard
                            photoUrl={photos.location?.url}
                            type="location"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                    </div>}

                    {/* 新版本：连拍功能 */}
                    {showMultiPhotoSelector && (
                        <>
                            {/* 多图画廊展示 */}
                            <div className="mt-4">
                                <MultiPhotoGalleryView
                                    photos={photosArray
                                        .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                        .map(photo => photo.url)
                                    }
                                    onDelete={(index: number) => {
                                        deletePhotoArray(index);
                                    }}
                                    maxDisplayCount={kMaxSelectorPhotoLength}
                                    onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                                />
                            </div>
                            {/* 相册上传 */}
                            {photosArray.length < kMaxSelectorPhotoLength &&
                                <div className="mt-4">
                                    {multiPhotoSelectorView}
                                </div>}
                        </>
                    )}
                </div>

                {/* 查看大图 */}
                <PhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={exceptionReason.isUploadsImages && (
                        showMultiPhotoSelector
                            ? (photosArray.length < photoConfig.minPhotos || isLoading)
                            : (!photos.label?.url || !photos.location?.url || isLoading)
                    )}
                    onClick={handleCompleteException}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default MarkExceptionPage;
